/**
 * Registro de Push Notifications con @capacitor/push-notifications.
 *
 * Alcance actual: ANDROID APK únicamente. La estructura queda lista para iOS
 * cuando se habilite (mismo plugin, sólo flipping un flag). En web nunca se
 * intenta nada — el flujo simplemente regresa sin efecto, sin lanzar errores
 * que puedan bloquear login u operación.
 */
import { api } from "@/lib/api";
import { isAndroidNative, getPlatform } from "@/lib/platform";

const AREA_ROLES = new Set(["COCINA", "BEBIDAS", "FREIDORA"]);
let _setupPromise = null;
let _registeredToken = null;

/** Devuelve true si el rol del usuario debe recibir notificaciones de área. */
export const userNeedsAreaPush = (user) => !!user && AREA_ROLES.has(user.role);

/** Limpia el estado (logout) para que el próximo login pueda registrar de nuevo. */
export const resetPushSetup = () => {
  _setupPromise = null;
  _registeredToken = null;
};

/**
 * Inicia el flujo de Push para el usuario autenticado. Idempotente: si ya
 * registramos en esta sesión, sólo retorna el token cacheado.
 *
 *   • Web → no-op (devuelve {skipped:'web'}).
 *   • iOS → no-op por ahora (devuelve {skipped:'ios-pending'}).
 *   • Android nativo → pide permisos, registra, captura token y lo guarda
 *     en backend vía POST /api/push/registro.
 *
 * Nunca lanza: cualquier error se loguea y se devuelve {error: '...'} para
 * que el caller pueda decidir si mostrar mensaje. No bloquea ningún flujo
 * operativo (login, órdenes, cocina, etc.).
 */
export async function setupPushNotifications(user) {
  if (!user) return { skipped: "no-user" };
  if (!userNeedsAreaPush(user)) return { skipped: "role-not-area" };

  const platform = getPlatform();
  if (!isAndroidNative()) {
    // iOS está reservado para más adelante; en web nunca tocamos nada.
    return { skipped: platform === "ios" ? "ios-pending" : "web" };
  }

  if (_setupPromise) return _setupPromise;

  _setupPromise = (async () => {
    const Push = window.Capacitor?.Plugins?.PushNotifications;
    if (!Push) {
      // El plugin no fue empaquetado en este build APK. No es bug del usuario;
      // sólo registramos y seguimos.
      console.warn("[push] PushNotifications plugin no disponible en este APK.");
      return { error: "plugin-missing" };
    }

    try {
      // 1) Permisos.
      let perm = await Push.checkPermissions();
      if (perm.receive !== "granted") {
        perm = await Push.requestPermissions();
      }
      if (perm.receive !== "granted") {
        return { error: "permission-denied" };
      }

      // 2) Registrar listeners ANTES de invocar register() para no perder eventos.
      const tokenPromise = new Promise((resolve, reject) => {
        const onReg = Push.addListener("registration", (t) => {
          resolve(t?.value || t);
        });
        const onErr = Push.addListener("registrationError", (e) => {
          reject(new Error(e?.error || "registration-error"));
        });
        // Cleanup tras 30s para no dejar listeners colgados si nunca llega.
        setTimeout(() => {
          try { onReg.then?.((h) => h?.remove?.()); } catch { /* ignore */ }
          try { onErr.then?.((h) => h?.remove?.()); } catch { /* ignore */ }
        }, 30000);
      });

      // 3) Pedir registro al sistema (FCM).
      await Push.register();

      // 4) Esperar el token y mandarlo al backend.
      const token = await tokenPromise;
      if (!token) return { error: "no-token" };

      try {
        await api.post("/push/registro", {
          token,
          platform: "android",
          device_label: navigator?.userAgent?.slice(0, 80),
        });
      } catch (err) {
        // Falló el POST al backend pero el token sí se obtuvo. Loguear y
        // devolver el token igual; el caller puede reintentar.
        console.warn("[push] backend register falló:", err?.message);
        return { token, error: "backend-failed" };
      }

      _registeredToken = token;
      return { token, platform: "android" };
    } catch (err) {
      console.warn("[push] setup falló:", err?.message);
      return { error: err?.message || "unknown" };
    }
  })();

  return _setupPromise;
}

export const getRegisteredToken = () => _registeredToken;
